/**
 * BRUNEL shared protocol - worker report stream envelope (ADR-0005).
 *
 * LOCKED ARCHITECTURE:
 * - Worker Chat and Worker Reports share stream infrastructure but remain
 *   semantically distinct.
 * - Reports are STRUCTURED operational events, suitable for Overseer
 *   summaries without replaying chat.
 * - Report payloads are non-secret; secret-shaped keys are rejected.
 */

export const REPORT_KINDS = [
  'status',
  'progress',
  'evidence',
  'question',
  'incident_note',
  'supervisor_synthesis',
] as const;

export type ReportKind = (typeof REPORT_KINDS)[number];

export type ReportPayloadValue = string | number | boolean;

export interface ReportEnvelope {
  readonly streamId: string;
  readonly reportKind: ReportKind;
  readonly payloadVersion: number;
  readonly summary: string;
  readonly payload: Readonly<Record<string, ReportPayloadValue>>;
  readonly createdAt: string;
}

/** Keys that suggest secret material; rejected in report payloads. */
export const SECRET_SHAPED_PAYLOAD_KEYS: readonly string[] = [
  'apikey',
  'api_key',
  'credential',
  'password',
  'privatekey',
  'private_key',
  'secret',
  'token',
];

/** Error thrown when a report envelope violates stream semantics. */
export class ReportValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ReportValidationError';
  }
}

function assertNonBlank(value: string, field: string): void {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new ReportValidationError(`report field '${field}' must be a non-blank string`);
  }
}

// ISO 8601 only: calendar date, optionally with a time (fractional seconds
// allowed) and timezone. Date-only and timezone-qualified timestamps are both
// accepted; looser Date-parseable forms like "Sep 17 2026" are not.
const ISO_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?)?$/;

function assertIsoTimestamp(value: string, field: string): void {
  if (!ISO_TIMESTAMP_PATTERN.test(value) || Number.isNaN(new Date(value).getTime())) {
    throw new ReportValidationError(`${field} must be an ISO timestamp`);
  }
}

function isPlainObject(value: unknown): value is Record<string, ReportPayloadValue> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Validates a structured report envelope. Free-form chat text is rejected
 * here; conversational content belongs to the chat stream, not reports.
 */
export function assertReportWellFormed(envelope: ReportEnvelope): void {
  assertNonBlank(envelope.streamId, 'streamId');
  if (!REPORT_KINDS.includes(envelope.reportKind)) {
    throw new ReportValidationError('reportKind must be one of the structured report kinds');
  }
  if (!Number.isInteger(envelope.payloadVersion) || envelope.payloadVersion < 1) {
    throw new ReportValidationError('payloadVersion must be a positive integer');
  }
  assertNonBlank(envelope.summary, 'summary');
  if (!isPlainObject(envelope.payload)) {
    throw new ReportValidationError('payload must be a plain object');
  }
  for (const [key] of Object.entries(envelope.payload)) {
    const normalized = key.toLowerCase();
    if (SECRET_SHAPED_PAYLOAD_KEYS.some((term) => normalized.includes(term))) {
      throw new ReportValidationError(`payload key '${key}' is secret-shaped; secrets never enter report streams`);
    }
  }
  assertIsoTimestamp(envelope.createdAt, 'createdAt');
}
